import store from "../store";

let refreshPromise = null;
let bootstrapPromise = null;
let bootstrapCompleted = false;

function getPersistedUser() {
  try {
    const rawUser = localStorage.getItem("USER_PROFILE");
    const user = rawUser ? JSON.parse(rawUser) : null;
    return user && typeof user === "object" ? user : {};
  } catch (error) {
    return {};
  }
}

export function getSessionToken() {
  // localStorage is shared across tabs, while Vuex is not. Prefer the shared
  // value so a tab can use a token another tab has just refreshed.
  try {
    if (typeof localStorage !== "undefined") {
      return localStorage.getItem("USER_TOKEN") || "";
    }
  } catch (error) {
    // Fall back to Vuex when browser storage is unavailable.
  }

  return store.state.user.token || "";
}

function getSessionEmail() {
  return store.state.user.user?.email || getPersistedUser().email || "";
}

function requestRefresh(email) {
  return Promise.resolve(store.dispatch("user/refreshToken", { email })).then(
    (token) => {
      if (typeof token !== "string" || !token) {
        throw new Error("No se pudo renovar la sesión");
      }
      return token;
    },
  );
}

function wait(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Comparte la renovación entre los clientes Axios de esta pestaña. Si otra
 * pestaña ya rotó la cookie, espera a que el navegador aplique Set-Cookie y
 * vuelve a intentarlo una vez con la cookie nueva.
 */
export function refreshSession() {
  if (!refreshPromise) {
    const email = getSessionEmail();

    refreshPromise = requestRefresh(email)
      .catch(async (error) => {
        if (error?.response?.status !== 409) throw error;
        await wait(120);
        return requestRefresh(email);
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

function tokenExpiresSoon(token) {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload || typeof atob !== "function") return true;
    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    const expiresAt = Number(payload.exp) * 1000;
    return !Number.isFinite(expiresAt) || expiresAt <= Date.now() + 60000;
  } catch (error) {
    return true;
  }
}

function hasDefinitiveRefreshFailure(error) {
  return [401, 403].includes(error?.response?.status);
}

/**
 * Restores the stored session before route guards make an authentication
 * decision. The refresh cookie remains usable even when local profile data is
 * missing, because the backend identifies its owner from that cookie.
 */
export function bootstrapSession({ required = false } = {}) {
  if (bootstrapCompleted) {
    return Promise.resolve(Boolean(store.state.user.loggedIn));
  }

  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      const stateUser = store.state.user.user;
      const persistedUser = stateUser?._id ? stateUser : getPersistedUser();
      const persistedUserId = persistedUser?._id;
      let token = getSessionToken();

      if (!required && !token && !persistedUserId) return false;

      if (persistedUserId && (!stateUser?._id || !store.state.user.loggedIn)) {
        await store.commit("user/setUser", persistedUser);
      }

      if (!token || tokenExpiresSoon(token)) {
        try {
          token = await refreshSession();
        } catch (error) {
          if (hasDefinitiveRefreshFailure(error)) {
            await store.commit("user/logout");
            return false;
          }

          // A network, rate-limit, or server error does not invalidate the
          // persisted session. Keep a cached user available for a later retry.
          return Boolean(store.state.user.user?._id || persistedUserId);
        }
      }

      if (!store.state.user.user?._id) {
        const result = await store.dispatch("user/fetchUserInformation");
        const restored = Boolean(result?.status && store.state.user.user?._id);
        if (restored) bootstrapCompleted = true;
        return restored;
      }

      bootstrapCompleted = true;
      return true;
    })().finally(() => {
      bootstrapPromise = null;
    });
  }

  return bootstrapPromise;
}
